import { Lock, PhoneOff } from "lucide-react";
import { useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { readableTextColor } from "../domain/deals/stageModel";
import { lastOutcomeText } from "../domain/leadDisplayStatus";
import type { CallOutcome } from "./types";
import { useLeadStatusColor } from "./useLeadStatuses";
import { useLeadStatusLabel } from "./useLeadStatusLabel";

/**
 * The lead's status (spec §4): the configured label on the configured colour, dark or white text by contrast; beside
 * it, in small grey text, the last call outcome and its age (none when the lead was never called).
 */
export const ProspectStatusBadge = ({
  status,
  outcome = null,
  calledAt = null,
  now,
}: {
  status: string;
  outcome?: CallOutcome | null;
  calledAt?: string | null;
  /** Tests pin the clock; the screens use the current time. */
  now?: Date;
}) => {
  const translate = useTranslate();
  const label = useLeadStatusLabel()(status);
  const color = useLeadStatusColor()(status);
  const last = lastOutcomeText(outcome, calledAt, now ?? new Date(), translate);
  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5">
      <span
        data-status={status}
        className="inline-flex max-w-full items-center rounded-full px-2 py-0.5 text-xs font-semibold"
        style={{ backgroundColor: color, color: readableTextColor(color) }}
      >
        <span className="truncate">{label}</span>
      </span>
      {last ? (
        <span
          data-testid="lead-last-outcome"
          className="text-xs text-muted-foreground"
        >
          {last}
        </span>
      ) : null}
    </span>
  );
};

/** A dot in the status's colour (the « Statut » filter). */
export const LeadStatusDot = ({ status }: { status: string }) => {
  const color = useLeadStatusColor()(status);
  return (
    <span
      aria-hidden="true"
      data-status-dot={status}
      className="inline-block size-2.5 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
};

export const CallOutcomeBadge = ({
  outcome,
}: {
  outcome: CallOutcome | null;
}) => {
  const translate = useTranslate();
  if (!outcome) return null;
  return (
    <Badge variant="outline">
      {translate(`gavion.call_outcome.${outcome}`)}
    </Badge>
  );
};

export const DncBadge = () => {
  const translate = useTranslate();
  return (
    <Badge variant="destructive" className="gap-1">
      <PhoneOff className="size-3" aria-hidden="true" />
      {translate("gavion.leads.badges.dnc")}
    </Badge>
  );
};

export const LockIndicator = () => {
  const translate = useTranslate();
  const label = translate("gavion.leads.badges.locked");
  return (
    <span
      className="inline-flex items-center text-muted-foreground"
      title={label}
    >
      <Lock className="size-3.5" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
};

/** GAVION test mode: a test lead (companies.is_test), called only in « Mode test », never in the statistics. */
export const TestBadge = () => {
  const translate = useTranslate();
  const hint = translate("gavion.test_mode.badge_hint");
  return (
    <Badge
      variant="outline"
      data-testid="test-lead-badge"
      className="border-amber-500 bg-amber-100 font-bold tracking-wide text-amber-900 dark:bg-amber-950 dark:text-amber-200"
      title={hint}
    >
      {translate("gavion.test_mode.badge")}
      <span className="sr-only"> — {hint}</span>
    </Badge>
  );
};
